import React, { useEffect } from "react";
import { useI18n } from "@/context/I18nContext";
import { legalDocs, UPDATED_LABEL } from "@/content/legal";
import { COMPANY } from "@/config/company";
import Seo from "@/components/Seo";
import { pageTitle, truncate } from "@/lib/seo";

const LEGAL_PATHS = { privacy: "/privacy", cookie: "/cookie-policy", terms: "/termini", refund: "/recesso-e-rimborsi" };

const LegalPage = ({ docKey }) => {
  const { lang } = useI18n();
  const L = lang === "en" ? "en" : "it";
  const doc = legalDocs[docKey]?.[L] || legalDocs[docKey]?.it;

  useEffect(() => { window.scrollTo({ top: 0 }); }, [docKey]);

  if (!doc) return null;
  const updatedLabel = L === "en" ? "Last updated" : "Ultimo aggiornamento";

  return (
    <div data-testid={`legal-${docKey}`}>
      <Seo
        title={pageTitle(doc.title)}
        description={truncate(doc.intro)}
        path={LEGAL_PATHS[docKey] || `/${docKey}`}
        type="website"
      />
      <section className="bg-brand-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-16">
          <h1 className="text-3xl md:text-4xl font-extrabold font-heading tracking-tight">{doc.title}</h1>
          <p className="mt-3 text-white/70 max-w-2xl text-sm md:text-base">{doc.intro}</p>
          <p className="mt-4 text-white/40 text-xs">{updatedLabel}: {UPDATED_LABEL}</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="rounded-2xl border border-brand-navy/10 bg-white p-6 mb-8" data-testid="legal-seller-box">
          <p className="text-xs uppercase tracking-widest text-brand-navy/40 font-semibold">
            {L === "en" ? "Seller" : "Venditore"}
          </p>
          <p className="mt-1 font-heading font-bold text-brand-navy">{COMPANY.seller}</p>
          <p className="text-sm text-brand-navy/60">{COMPANY.brand} — {COMPANY.status[L]}</p>
          <p className="text-sm text-brand-navy/60 mt-1">
            <a href={`mailto:${COMPANY.supportEmail}`} className="text-brand-electric hover:underline">{COMPANY.supportEmail}</a>
            {" · "}
            <a href={COMPANY.site} target="_blank" rel="noreferrer" className="text-brand-electric hover:underline">{COMPANY.site}</a>
          </p>
        </div>

        <div className="space-y-8">
          {doc.sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${i}`}>
              <h2 className="font-heading font-bold text-lg text-brand-navy">{s.h}</h2>
              {(s.p || []).map((para, j) => (
                <p key={j} className="mt-2 text-sm leading-relaxed text-brand-navy/70">{para}</p>
              ))}
              {s.ul && (
                <ul className="mt-3 space-y-2 list-disc pl-5">
                  {s.ul.map((li, k) => (
                    <li key={k} className="text-sm leading-relaxed text-brand-navy/70">{li}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
